import { Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { type ConnectedLevel, ConnectedLevels } from "../ConnectedLevels";
import { SessionReportScoreDonut } from "../SessionReport/Summary/SessionReportScoreDonut";
import TooltipWrapper from "../TooltipWrapper";
import {
	type ProficiencyScoresEmpty,
	ProficiencyScoresEmptyState,
} from "./ProficiencyScoresEmptyState";
import { ProgressCardShell } from "./ProgressCardShell";

export type ProficiencyScoreDonut = {
	value: number;
	maxScore: number;
	label?: string;
	subLabel?: string;
};

/**
 * The card renders exactly one of these. A discriminator rather than a set of
 * optional props: an absent `scores` array used to mean "show levels", which
 * left no way to say "score mode, but nothing scored yet".
 */
export type ProficiencyCardContent =
	| { kind: "levels"; levels: ConnectedLevel[]; tooltip?: string }
	| { kind: "scores"; scores: ProficiencyScoreDonut[] }
	| { kind: "noScores"; empty: ProficiencyScoresEmpty };

type Props = {
	title: string;
	subtitle?: string;
	icon?: IconType;
	content: ProficiencyCardContent;
	tip?: string;
	fillHeight?: boolean;
	tipLoading?: boolean;
};

const ScoreDonutColumn = ({
	donut,
	size,
}: {
	donut: ProficiencyScoreDonut;
	size: "summary" | "large";
}) => {
	const theme = useAppTheme();

	return (
		<Box gap={16} alignItems="center">
			<SessionReportScoreDonut
				score={donut.value}
				maxScore={donut.maxScore}
				progress={donut.maxScore > 0 ? donut.value / donut.maxScore : 0}
				size={size}
			/>
			{donut.label || donut.subLabel ? (
				<Box gap={4} alignItems="center">
					{donut.label ? (
						<Text variant="titleSmall" style={{ textAlign: "center" }}>
							{donut.label}
						</Text>
					) : null}
					{donut.subLabel ? (
						<Text
							variant="bodySmall"
							style={{
								color: theme.colors.onSurfaceVariant,
								textAlign: "center",
							}}
						>
							{donut.subLabel}
						</Text>
					) : null}
				</Box>
			) : null}
		</Box>
	);
};

const ProficiencyCardBody = ({
	content,
}: { content: ProficiencyCardContent }) => {
	const { isMobile } = useDevice();

	if (content.kind === "noScores") {
		return <ProficiencyScoresEmptyState {...content.empty} />;
	}

	if (content.kind === "scores") {
		return (
			<Box
				flexDirection="row"
				justifyContent="center"
				alignItems="flex-start"
				gap={isMobile ? 24 : 40}
			>
				{content.scores.map((donut, index) => (
					<ScoreDonutColumn
						key={donut.label ?? index}
						donut={donut}
						size={content.scores.length > 1 ? "summary" : "large"}
					/>
				))}
			</Box>
		);
	}

	return (
		<Box width="100%">
			{content.tooltip ? (
				<TooltipWrapper title={content.tooltip} display="block">
					<ConnectedLevels levels={content.levels} size="lg" />
				</TooltipWrapper>
			) : (
				<ConnectedLevels levels={content.levels} size="lg" />
			)}
		</Box>
	);
};

export const ProficiencyLevelsCard = ({
	title,
	subtitle,
	icon = "book-open-outline",
	content,
	tip,
	fillHeight,
	tipLoading,
}: Props) => {
	const { isMobile } = useDevice();

	// An AI insight on a score that does not exist would be invented commentary,
	// so that stays suppressed in the empty state. The header subtitle is fine
	// to keep, though — it's static framing ("Based on your overall study
	// activity"), not a claim about performance.
	const hasScores = content.kind !== "noScores";

	return (
		<ProgressCardShell
			icon={icon}
			title={title}
			subtitle={subtitle}
			tip={hasScores ? tip : undefined}
			fillHeight={fillHeight}
			tipLoading={hasScores ? tipLoading : undefined}
		>
			<Box
				flex={1}
				alignItems="center"
				justifyContent="center"
				paddingVertical={isMobile ? 24 : 42}
				paddingHorizontal={16}
			>
				<ProficiencyCardBody content={content} />
			</Box>
		</ProgressCardShell>
	);
};
